import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

import ConfirmDialog from '../components/ConfirmDialog'

interface ConfirmState {
  isOpen: boolean
  title: string
  message: string
  onConfirm: () => Promise<void>
  onCancel: () => void
}

interface ConfirmContextType {
  showConfirm: (
    title: string,
    message: string,
    onConfirm?: () => Promise<void>,
    onCancel?: () => void,
  ) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined)

// Hook to access the confirm context
export const useConfirm = (): ConfirmContextType => {
  const context = useContext(ConfirmContext)
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider')
  }
  return context
}

// Confirm Provider component
export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => Promise.resolve(),
    onCancel: () => null,
  })
  const closeModal = (): void => {
    setConfirmState((previous) => ({
      ...previous,
      isOpen: false,
    }))
  }

  // Function to show the confirm modal, wrapped in useCallback for memoization
  const showConfirm = useCallback(
    (
      title: string,
      message: string,
      onConfirm?: () => Promise<void>,
      onCancel?: VoidFunction,
    ): Promise<boolean> => {
      if (!title || !message) {
        throw new Error('Title and message are required for showConfirm')
      }
      return new Promise((resolve) => {
        setConfirmState({
          isOpen: true,
          title,
          message,
          onConfirm: async () => {
            onConfirm?.()
            resolve(true)
            closeModal()
          },
          onCancel: () => {
            onCancel?.()
            resolve(false)
            closeModal()
          },
        })
      })
    },
    [],
  )

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ showConfirm }), [showConfirm])

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}

      <ConfirmDialog
        open={confirmState.isOpen}
        isLoading={false}
        title={confirmState.title}
        content={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={confirmState.onCancel}
      />
    </ConfirmContext.Provider>
  )
}
